import {
  GroupRole,
  OrgRole,
  PORTAL_GROUP_ROLES,
  PORTAL_ORG_ROLES,
  PortalGroupRole,
  PortalOrgRole,
  PortalRole,
  Role,
  TRIAL_GROUP_ROLES,
  TRIAL_ORG_ROLES,
  TrialGroupRole,
  TrialOrgRole,
  TrialRole,
} from "Types";

export const IS_TRIAL = window.env?.isTrial.value === true;

// Portal org roles
export const ORG_ADMIN_ROLE: PortalRole = {
  type: "organization",
  value: "OrganizationAdmin",
};
export const ORG_MEMBER_ROLE: PortalRole = {
  type: "organization",
  value: "OrganizationMember",
};
export const ORG_SECURITY_ROLE: PortalRole = {
  type: "organization",
  value: "Security",
};
export const RL_SUPPORT_ROLE: PortalRole = {
  type: "organization",
  value: "RlSupport",
};

// Portal group roles
export const GROUP_OWNER_ROLE: PortalRole = {
  type: "group",
  value: "GroupOwner",
};
export const GROUP_MEMBER_ROLE: PortalRole = {
  type: "group",
  value: "GroupMember",
};
export const GROUP_OBSERVER_ROLE: PortalRole = {
  type: "group",
  value: "GroupObserver",
};
export const GROUP_MAINTAINER: PortalRole = {
  type: "group",
  value: "Maintainer",
};

// Trial roles
export const TRIAL_SUPPORT_ROLE: TrialRole = {
  type: "organization",
  value: "RlSupport",
};
export const TRIAL_ORG_ADMIN_ROLE: TrialRole = {
  type: "organization",
  value: "OrganizationAdmin",
};
export const TRIAL_ORG_MEMBER_ROLE: TrialRole = {
  type: "organization",
  value: "TrialOrganizationMember",
};
export const TRIAL_GROUP_MEMBER_ROLE: TrialRole = {
  type: "group",
  value: "TrialGroupMember",
};

export type RoleDetails = {
  id: string;
  label: string;
  shortLabel: string;
  helperText: string;
} & Role;

const portalOrgRoleDetails: Record<PortalOrgRole, RoleDetails> = {
  OrganizationAdmin: {
    id: "organization-admin-role",
    type: ORG_ADMIN_ROLE.type,
    value: ORG_ADMIN_ROLE.value,
    label: "Organization administrator",
    shortLabel: "Administrator",
    helperText:
      "Create and delete groups, add and remove organization members, \
      modify member roles, upload files in file stream, export data, \
      administer projects and versions, approve and revoke versions, \
      import software from the ReversingLabs catalog, publish and unpublish \
      software in the group catalog, configure policy profiles, configure SSO, \
      configure session lifetime, manage portal and CLI capacity, \
      configure AI settings, view audit logs, plus all Organization member permissions",
  },
  OrganizationMember: {
    id: "organization-member-role",
    type: ORG_MEMBER_ROLE.type,
    value: ORG_MEMBER_ROLE.value,
    label: "Organization member",
    shortLabel: "Member",
    helperText:
      "View groups, organization members, and organization policy profile. \
      View organization capacity, CLI capacity, usage, and monthly usage summaries per group",
  },
  Security: {
    id: "organization-security-role",
    type: ORG_SECURITY_ROLE.type,
    value: ORG_SECURITY_ROLE.value,
    label: "Organization security",
    shortLabel: "Security",
    helperText:
      "Edit software information in file stream, approve and revoke files and versions, \
      browse and download software from the catalog, share reports, configure policy profiles, \
      export data, trigger password resets and view audit logs, \
      plus all Organization member permissions",
  },
  RlSupport: {
    id: "rl-support-role",
    type: RL_SUPPORT_ROLE.type,
    value: RL_SUPPORT_ROLE.value,
    label: "ReversingLabs Support",
    shortLabel: "Support",
    helperText:
      "Special role used by ReversingLabs to assist users. Permissions are \
      similar to Organization administrator",
  },
};

const trialOrgRoleDetails: Record<TrialOrgRole, RoleDetails> = {
  RlSupport: {
    id: "rl-support-role",
    type: RL_SUPPORT_ROLE.type,
    value: RL_SUPPORT_ROLE.value,
    label: "ReversingLabs Support",
    shortLabel: "Support",
    helperText:
      "Special role used by ReversingLabs to assist users. Permissions are \
      similar to Organization administrator",
  },
  OrganizationAdmin: {
    id: "organization-admin-role",
    type: ORG_ADMIN_ROLE.type,
    value: ORG_ADMIN_ROLE.value,
    label: "Organization administrator",
    shortLabel: "Administrator",
    helperText:
      "Create and delete groups, add and remove organization members, \
      modify member roles, upload files in file stream, export data, \
      administer projects and versions, approve and revoke versions, \
      import software from the ReversingLabs catalog, publish and unpublish \
      software in the group catalog, configure policy profiles, configure SSO, \
      configure session lifetime, manage portal and CLI capacity, \
      configure AI settings, view audit logs, plus all Organization member permissions",
  },
  TrialOrganizationMember: {
    id: "trial-organization-member-role",
    type: TRIAL_ORG_MEMBER_ROLE.type,
    value: TRIAL_ORG_MEMBER_ROLE.value,
    label: "Trial organization member",
    shortLabel: "Trial member",
    helperText:
      "View organization policy profile, view organization capacity and usage",
  },
};

const portalGroupRoleDetails: Record<PortalGroupRole, RoleDetails> = {
  GroupOwner: {
    id: "group-owner-role",
    type: GROUP_OWNER_ROLE.type,
    value: GROUP_OWNER_ROLE.value,
    label: "Group owner",
    shortLabel: "Owner",
    helperText:
      "Can add and remove members from a group, \
       change group role for a member, override group policy profile, modify artifact approval status, \
       plus all Maintainer permission",
  },
  Maintainer: {
    id: "group-maintainer-role",
    type: GROUP_MAINTAINER.type,
    value: GROUP_MAINTAINER.value,
    label: "Maintainer",
    shortLabel: "Maintainer",
    helperText:
      "Can create, edit, reanalyze, and delete projects and packages, \
       upload versions to package, delete version, mark version as released, \
       edit version software information, import software from the ReversingLabs catalog, \
       publish and unpublish software in the group catalog, share reports, \
       plus all Group member permissions",
  },
  GroupMember: {
    id: "group-member-role",
    type: GROUP_MEMBER_ROLE.type,
    value: GROUP_MEMBER_ROLE.value,
    label: "Group member",
    shortLabel: "Member",
    helperText:
      "Can view group members, upload files to file stream, edit file software information, \
      view projects, packages and versions, download files and versions, export data, \
      browse and download software from the catalog, search insights, \
      view group policy profile, view group usage",
  },
  GroupObserver: {
    id: "group-observer-role",
    type: GROUP_OBSERVER_ROLE.type,
    value: GROUP_OBSERVER_ROLE.value,
    label: "Group observer",
    shortLabel: "Observer",
    helperText:
      "Can view group members, view projects, packages and versions, \
       download files and versions, export data, \
       browse and download software from the catalog, search insights, \
       view group policy profile, view group usage",
  },
};

const trialGroupRoleDetails: Record<TrialGroupRole, RoleDetails> = {
  TrialGroupMember: {
    id: "trial-group-member-role",
    type: TRIAL_GROUP_MEMBER_ROLE.type,
    value: TRIAL_GROUP_MEMBER_ROLE.value,
    label: "Trial group member",
    shortLabel: "Trial member",
    helperText:
      "Upload files to file stream, create projects, packages and versions, \
      approve and revoke versions, publish software to the catalog, search insights, \
      view group policy profile",
  },
};

type RoleLabels = {
  [key in OrgRole | GroupRole]?: RoleDetails;
};

export const ORG_ROLE_DETAILS: RoleLabels = IS_TRIAL
  ? trialOrgRoleDetails
  : portalOrgRoleDetails;

export const ORG_ROLES: ReadonlyArray<OrgRole> = IS_TRIAL
  ? TRIAL_ORG_ROLES
  : PORTAL_ORG_ROLES;

export const GROUP_ROLE_DETAILS: RoleLabels = IS_TRIAL
  ? trialGroupRoleDetails
  : portalGroupRoleDetails;

export const GROUP_ROLES: ReadonlyArray<GroupRole> = IS_TRIAL
  ? TRIAL_GROUP_ROLES
  : PORTAL_GROUP_ROLES;

export const roleLabels: RoleLabels = {
  ...ORG_ROLE_DETAILS,
  ...GROUP_ROLE_DETAILS,
};

export const DEFAULT_ORG_ROLE: OrgRole = IS_TRIAL
  ? "TrialOrganizationMember"
  : "OrganizationMember";

export const DEFAULT_GROUP_ROLE: GroupRole = IS_TRIAL
  ? "TrialGroupMember"
  : "GroupMember";

export const GROUP_ROLE_TO_STRING: Record<GroupRole, string> = {
  GroupMember: "Member",
  GroupOwner: "Owner",
  TrialGroupMember: "Member",
  GroupObserver: "Observer",
  Maintainer: "Maintainer",
};

export const ORG_ROLE_TO_STRING: Record<OrgRole, string> = {
  OrganizationAdmin: "Administrator",
  OrganizationMember: "Member",
  Security: "Security",
  RlSupport: "Support",
  TrialOrganizationMember: "Member",
};
